/**
 * TOTP enrolment UI. Auto-creates a factor on mount, shows the QR code +
 * manual key, and verifies the 6-digit code. Works with Google Authenticator,
 * Microsoft Authenticator, Authy, 1Password, etc.
 */
import { useEffect, useRef, useState } from "react";
import { Loader2, AlertCircle, CheckCircle2, Copy, ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { enrollTotp, verifyTotp, type TotpEnrollment } from "@/lib/auth/mfa";

const GRAD = "#5048E5";
const fg = "hsl(var(--foreground))";
const fgMuted = "hsl(var(--muted-foreground))";
const border = "hsl(var(--border))";
const muted = "hsl(var(--muted))";

let activeEnrollment: TotpEnrollment | null = null;
let activeEnrollmentPromise: Promise<TotpEnrollment> | null = null;
let activeEnrollmentUserId: string | null = null;

const STORE_PREFIX = "db-mfa-enrollment:";

function storeKey(userId: string | null) {
  return `${STORE_PREFIX}${userId ?? "anon"}`;
}

/**
 * Persist the in-progress enrolment so the SAME QR code / secret / factor is
 * shown after the user leaves to open their authenticator app and the mobile
 * browser discards & restores the tab. Without this, a fresh enrolment would
 * generate a new QR every return trip, so the code never matches.
 */
function saveEnrollment(userId: string | null, enrollment: TotpEnrollment) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(storeKey(userId), JSON.stringify(enrollment));
  } catch {
    /* ignore quota / serialization errors */
  }
}

function loadEnrollment(userId: string | null): TotpEnrollment | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(storeKey(userId));
    return raw ? (JSON.parse(raw) as TotpEnrollment) : null;
  } catch {
    return null;
  }
}

function dropEnrollment(userId: string | null) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(storeKey(userId));
  } catch {
    /* ignore */
  }
}

async function getStableEnrollment() {
  const { data } = await supabase.auth.getUser();
  const userId = data.user?.id ?? null;
  if (activeEnrollment && activeEnrollmentUserId === userId) return activeEnrollment;
  if (activeEnrollmentUserId !== userId) {
    clearStableEnrollment();
    activeEnrollmentUserId = userId;
  }
  // Restore a persisted enrolment (survives tab discard/restore) before
  // creating a new factor — this keeps the QR code stable across app switches.
  const persisted = loadEnrollment(userId);
  if (persisted) {
    activeEnrollment = persisted;
    activeEnrollmentUserId = userId;
    return persisted;
  }
  if (!activeEnrollmentPromise) {
    activeEnrollmentPromise = enrollTotp().then((next) => {
      activeEnrollment = next;
      activeEnrollmentUserId = userId;
      saveEnrollment(userId, next);
      return next;
    }).finally(() => {
      activeEnrollmentPromise = null;
    });
  }
  return activeEnrollmentPromise;
}

function clearStableEnrollment() {
  dropEnrollment(activeEnrollmentUserId);
  activeEnrollment = null;
  activeEnrollmentPromise = null;
  activeEnrollmentUserId = null;
}

export default function MfaEnroll({
  onVerified,
  onCancel,
}: {
  onVerified: () => void;
  onCancel?: () => void;
}) {
  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [failures, setFailures] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        setEnrollment(await getStableEnrollment());
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not start setup.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    if (!enrollment) return;
    setVerifying(true);
    setError("");
    try {
      await verifyTotp(enrollment.factorId, code);
      clearStableEnrollment();
      onVerified();
    } catch {
      setError("That code didn't match. Wait for the next code and try again.");
      setFailures((n) => n + 1);
      setCode("");
      setVerifying(false);
    }
  }

  function copySecret() {
    if (!enrollment) return;
    navigator.clipboard?.writeText(enrollment.secret).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 size={22} className="animate-spin" style={{ color: GRAD }} />
      </div>
    );
  }

  if (!enrollment) {
    return (
      <div className="flex items-center gap-2.5 rounded-xl p-3.5 text-sm"
        style={{ background: "hsla(0,72%,51%,0.08)", border: "1px solid hsla(0,72%,51%,0.2)", color: "#ef4444", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
        <AlertCircle size={15} className="shrink-0" />
        {error || "Could not start two-factor setup."}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <ol className="flex flex-col gap-1.5 text-sm" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
        <li>1. Open your authenticator app (Google, Microsoft, Authy, 1Password…).</li>
        <li>2. Scan the QR code below, or enter the key manually.</li>
        <li>3. Type the 6-digit code it shows to confirm.</li>
      </ol>

      <div className="flex flex-col items-center gap-4 rounded-2xl p-5 sm:flex-row sm:items-center"
        style={{ background: muted, border: `1px solid ${border}` }}>
        <img
          src={enrollment.qrCode}
          alt="Two-factor QR code"
          className="h-40 w-40 shrink-0 rounded-xl bg-white p-2"
          width={160}
          height={160}
        />
        <div className="flex w-full flex-col gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
            Can't scan? Enter this key
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded-lg px-3 py-2 text-xs"
              style={{ background: "hsl(var(--background))", border: `1px solid ${border}`, color: fg, fontFamily: "monospace" }}>
              {enrollment.secret}
            </code>
            <button type="button" onClick={copySecret} aria-label="Copy key"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-opacity hover:opacity-80"
              style={{ background: "hsl(var(--background))", border: `1px solid ${border}`, color: copied ? "#22c55e" : fgMuted }}>
              {copied ? <CheckCircle2 size={15} /> : <Copy size={15} />}
            </button>
          </div>
        </div>
      </div>

      <form onSubmit={handleVerify} className="flex flex-col gap-3">
        <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
          6-digit code
        </label>
        <input
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={6}
          value={code}
          onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(""); }}
          placeholder="123456"
          className="w-full rounded-xl px-4 py-3 text-center text-lg tracking-[0.3em] outline-none"
          style={{ background: muted, border: `1px solid ${border}`, color: fg, fontFamily: "monospace" }}
        />

        {error && (
          <p className="flex items-start gap-1.5 text-xs" style={{ color: "#ef4444", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
            <AlertCircle size={13} className="mt-px shrink-0" /> {error}
          </p>
        )}

        {failures >= 1 && (
          <div className="flex flex-col gap-1.5 rounded-xl p-3.5 text-xs"
            style={{ background: muted, border: `1px solid ${border}`, color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
            <p className="font-semibold" style={{ color: fg }}>Code still not matching? Try this:</p>
            <p>• In Microsoft Authenticator, <strong>delete any older "Digitally Baffled" entry</strong> and re-scan the QR above — old entries keep showing codes that no longer work.</p>
            <p>• Make sure your phone's clock is set to <strong>automatic / network time</strong> — TOTP codes fail if the clock drifts.</p>
            <p>• On Android, open Authenticator → <strong>Settings → Time correction for codes → Sync now</strong>.</p>
            <p>• Enter the code right when it refreshes, so it doesn't expire mid-typing.</p>
          </div>
        )}

        <button type="submit" disabled={verifying || code.length !== 6}
          className="flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold transition-all hover:opacity-90 disabled:opacity-50"
          style={{ background: GRAD, color: "#fff", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
          {verifying ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />}
          {verifying ? "Verifying…" : "Turn on two-factor"}
        </button>

        {onCancel && (
          <button type="button" onClick={() => { clearStableEnrollment(); onCancel(); }} disabled={verifying}
            className="rounded-xl py-2.5 text-sm font-semibold transition-all hover:opacity-90 disabled:opacity-60"
            style={{ background: muted, border: `1px solid ${border}`, color: fg, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
            Cancel
          </button>
        )}
      </form>
    </div>
  );
}
